// The website chat: Chatwoot's web widget (inbox 140268), answered by Aider,
// the coach (docs/CHATWOOT.md, Website chat). Robin, 25 Oct 2026: "now that
// we have Aider we should use it as going straight to whatsapp feels jarring
// unless they're on mobile or desktop app exists".
//
// Nothing of Chatwoot is in our bundle. Its SDK script is injected on the
// first tap of the chat button, never on page load, and the widget opens
// with its own launcher bubble hidden: our floating button is the way in
// (components/whatsapp-fab), and while the chat is open on a desktop it
// becomes the close button with "Continue on WhatsApp" beside it.
//
// Who is chatting (one person, one contact): the page asks our server for a
// signed identity (GET /api/v2/chat_identity, Chatwoot identity validation)
// and hands it to the widget's setUser. A signed-in customer is their own
// contact; anyone else is this browser's random chat reference
// (lib/chat-entry.js chatRef), which the "Continue on WhatsApp" message
// carries so the WhatsApp thread joins the same contact.

export const CHATWOOT_BASE_URL = 'https://app.chatwoot.com'
export const CHATWOOT_SDK_URL = `${CHATWOOT_BASE_URL}/packs/js/sdk.js`

// The public website token, written by the layout. Blank: no website chat.
export const chatWidgetToken = (win = globalThis.window) => {
  const token = win && win.__CHATWOOT_WEBSITE_TOKEN
  return typeof token === 'string' ? token.trim() : ''
}

// With the bubble hidden Chatwoot sits the open chat at the very bottom;
// lifted on a wide screen so our button, now close + "Continue on WhatsApp",
// stays in view under it. Phones keep Chatwoot's full screen.
const HOLDER_CSS =
  '@media (min-width: 668px){.woot-widget-holder.woot-widget--without-bubble{bottom:92px!important;' +
  'max-height:calc(100vh - 116px)!important}}'

// Inject the SDK once and resolve when the widget is ready (window.$chatwoot).
// A second call returns the same promise. Rejects if the script fails or
// the widget is not ready in `timeoutMs`, so the caller can offer WhatsApp.
let loading = null
export const loadChatWidget = ({ token, win = globalThis.window, doc = globalThis.document, timeoutMs = 15000 }) => {
  if (win.$chatwoot && win.$chatwoot.hasLoaded) return Promise.resolve(win.$chatwoot)
  if (loading) return loading

  loading = new Promise((resolve, reject) => {
    const timer = setTimeout(() => fail(new Error('chat widget timed out')), timeoutMs)
    const ready = () => { clearTimeout(timer); resolve(win.$chatwoot) }
    function fail (err) { clearTimeout(timer); loading = null; reject(err) }

    win.chatwootSettings = { hideMessageBubble: true, position: 'right', locale: 'en', type: 'standard', darkMode: 'light' }
    win.addEventListener('chatwoot:ready', ready, { once: true })
    watchOpen(win)

    const style = doc.createElement('style')
    style.textContent = HOLDER_CSS
    doc.head.appendChild(style)

    const script = doc.createElement('script')
    script.src = CHATWOOT_SDK_URL
    script.async = true
    script.defer = true
    script.dataset.testid = 'chatwoot-sdk'
    script.onload = () => {
      try {
        win.chatwootSDK.run({ websiteToken: token, baseUrl: CHATWOOT_BASE_URL })
      } catch (err) {
        fail(err)
      }
    }
    script.onerror = () => fail(new Error('chat widget did not load'))
    doc.head.appendChild(script)
  })
  return loading
}

// The signed identity for setUser, or null (anonymous). Never throws: a
// chat without an identity still works.
export const fetchChatIdentity = async ({ host = '', ref, userToken, fetchFn = globalThis.fetch }) => {
  try {
    const headers = userToken ? { 'X-User-Token': userToken } : {}
    const res = await fetchFn(`${host}/api/v2/chat_identity?ref=${encodeURIComponent(ref || '')}`, { headers })
    if (!res.ok) return null
    const data = await res.json()
    const identity = data && data.identity
    return identity && identity.identifier && identity.identifier_hash ? identity : null
  } catch (e) {
    return null
  }
}

// Tell Chatwoot who this is (when we can sign it), what to call the chat
// reference, then open the widget. No name, email or phone leaves here.
export const openChatWidget = (chatwoot, { identity, ref, posthogId }) => {
  if (identity && typeof chatwoot.setUser === 'function') {
    chatwoot.setUser(identity.identifier, { identifier_hash: identity.identifier_hash })
  }
  const attributes = {}
  if (ref) attributes.chat_ref = String(ref)
  if (posthogId) attributes.posthog_distinct_id = String(posthogId)
  if (Object.keys(attributes).length) chatwoot.setCustomAttributes(attributes)
  chatwoot.toggle('open')
  setOpen(true)
}

export const closeChatWidget = (win = globalThis.window) => {
  if (win && win.$chatwoot && typeof win.$chatwoot.toggle === 'function') win.$chatwoot.toggle('close')
  setOpen(false)
}

// Open or closed, for our button. Chatwoot fires chatwoot:opened and
// chatwoot:closed (its own close button included); toggles we make set it
// too, for SDK versions that do not.
let open = false
const listeners = new Set()
const setOpen = value => {
  if (open === !!value) return
  open = !!value
  listeners.forEach(fn => fn(open))
}
export const chatIsOpen = () => open
export const onChatOpenChange = fn => { listeners.add(fn); return () => listeners.delete(fn) }

// The first message of a chat, once per page (chat_message_sent).
let started = []
export const onChatStarted = fn => { started.push(fn); return () => { started = started.filter(f => f !== fn) } }

let watching = false
function watchOpen (win) {
  if (watching || !win.addEventListener) return
  watching = true
  win.addEventListener('chatwoot:opened', () => setOpen(true))
  win.addEventListener('chatwoot:closed', () => setOpen(false))
  win.addEventListener('chatwoot:on-start-conversation', () => started.forEach(fn => fn()))
}

// For tests only.
export const resetChatWidgetLoader = () => { loading = null; open = false; watching = false; listeners.clear(); started = [] }
